// ─── charts.js ────────────────────────────────────────────────────────────────
// Alle grafiekfuncties: balk, 3D balk, lijn, taart/donut, horizontale balk.
// Wijzig hier bij aanpassingen aan grafiekstijlen.

import { fmtK, fmtE } from './helpers.js';

function balkGrafiek(data, labels, kleur, maxHoogte=90) {
  kleur = kleur || '#1a364c';
  const max = Math.max(...data.filter(Boolean), 1);
  const n = data.length;
  const W = 480, H = maxHoogte, yW = 46, xH = 18;
  const totW = W + yW;
  const totH = H + xH + 14; // ruimte voor waarde label boven balk
  const barW = Math.max(2, Math.floor((W / n) * 0.50));
  const gap = (W - barW * n) / (n + 1);
  const ySteps = [0, 0.25, 0.5, 0.75, 1];

  return `<svg width="100%" viewBox="0 0 ${totW} ${totH}" style="display:block;overflow:visible;">
    <!-- Y-as labels -->
    ${ySteps.map(p=>`
      <text x="${yW-4}" y="${14 + H - Math.round(p*H) + 3}" font-size="8" fill="#94a3b8" text-anchor="end">${fmtK(max*p)}</text>
      <line x1="${yW}" y1="${14 + H - Math.round(p*H)}" x2="${totW}" y2="${14 + H - Math.round(p*H)}" stroke="${p===0?'#cbd5e1':'#f1f5f9'}" stroke-width="${p===0?1:0.5}"/>
    `).join('')}
    <!-- Balken + labels -->
    ${data.map((val,i)=>{
      const bH = val ? Math.max(Math.round((val/max)*H), 3) : 2;
      const x = yW + gap + i*(barW+gap);
      const y = 14 + H - bH;
      const cx = x + barW/2;
      return `
        <rect x="${x}" y="${y}" width="${barW}" height="${bH}" rx="2"
          fill="${val ? kleur : '#f1f5f9'}" opacity="${val ? 1 : 0.4}"/>
        ${val>0 ? `<text x="${cx}" y="${y-3}" font-size="7" fill="#64748b" text-anchor="middle">${fmtK(val)}</text>` : ''}
        <text x="${cx}" y="${14+H+xH-2}" font-size="8" fill="#94a3b8" text-anchor="middle">${labels[i]}</text>
      `;
    }).join('')}
  </svg>`;
}

function balkGrafiek3D(data, labels, kleur, maxHoogte=120) {
  kleur = kleur || '#1a364c';
  const max = Math.max(...data.filter(Boolean), 1);
  const n = data.length;
  const W = 480, H = maxHoogte, yW = 46, xH = 18, depth = 6;
  const totW = W + yW + depth;
  const totH = H + xH + 14 + depth;
  const barW = Math.max(2, Math.floor((W / n) * 0.6));
  const gap = (W - barW * n) / (n + 1);
  const ySteps = [0, 0.25, 0.5, 0.75, 1];
  // Lighter/darker shades for 3D effect
  const kleurDark = kleur === '#1a364c' ? '#0f2030' : kleur;
  const kleurTop  = kleur === '#1a364c' ? '#2d5a7a' : kleur;

  return `<svg width="100%" viewBox="0 0 ${totW} ${totH}" style="display:block;overflow:visible;">
    <!-- Y-as labels -->
    ${ySteps.map(p=>`
      <text x="${yW-4}" y="${14 + H - Math.round(p*H) + 3}" font-size="8" fill="#94a3b8" text-anchor="end">${fmtK(max*p)}</text>
      <line x1="${yW}" y1="${14 + H - Math.round(p*H)}" x2="${totW-depth}" y2="${14 + H - Math.round(p*H)}" stroke="${p===0?'#cbd5e1':'#f1f5f9'}" stroke-width="${p===0?1:0.5}"/>
    `).join('')}
    <!-- 3D Balken -->
    ${data.map((val,i)=>{
      if(!val) return '';
      const bH = Math.max(Math.round((val/max)*H), 3);
      const x = yW + gap + i*(barW+gap);
      const y = 14 + H - bH;
      const cx = x + barW/2;
      // Front face
      const front = `<rect x="${x}" y="${y}" width="${barW}" height="${bH}" fill="${kleur}"/>`;
      // Top face (parallelogram)
      const top = `<polygon points="${x},${y} ${x+depth},${y-depth} ${x+barW+depth},${y-depth} ${x+barW},${y}" fill="${kleurTop}" opacity="0.85"/>`;
      // Right face
      const right = `<polygon points="${x+barW},${y} ${x+barW+depth},${y-depth} ${x+barW+depth},${y-depth+bH} ${x+barW},${y+bH}" fill="${kleurDark}" opacity="0.7"/>`;
      return `<g>
        ${right}${front}${top}
        ${val>0?`<text x="${cx}" y="${y-depth-3}" font-size="7" fill="#64748b" text-anchor="middle">${fmtK(val)}</text>`:''}
        <text x="${cx}" y="${14+H+xH-2}" font-size="8" fill="#94a3b8" text-anchor="middle">${labels[i]}</text>
      </g>`;
    }).join('')}
  </svg>`;
}

function lijnGrafiek(data, labels, kleur, maxHoogte=90, data2=null, kleur2='#c2e6ef') {
  kleur = kleur || '#1a364c';
  const max = Math.max(...data.filter(Boolean), ...(data2||[]).filter(Boolean), 1);
  const W = 500, H = maxHoogte;
  const n = data.length;
  const xStep = W / Math.max(n-1, 1);
  const yAxisW = 44;

  const pts = data.map((val,i) => {
    const x = i * xStep;
    const y = val ? H - Math.round((val/max)*H) : H;
    return {x, y, val};
  });

  const polyline = pts.map(p=>`${p.x},${p.y}`).join(' ');
  const area = `${pts[0].x},${H} ${polyline} ${pts[pts.length-1].x},${H}`;
  const ySteps = [0, 0.25, 0.5, 0.75, 1];

  return `
    <div style="display:flex;gap:0;">
      <div style="width:${yAxisW}px;flex-shrink:0;display:flex;flex-direction:column;justify-content:space-between;padding-bottom:18px;">
        ${ySteps.slice().reverse().map(p=>`<div style="font-size:8px;color:#94a3b8;text-align:right;line-height:1;">${fmtK(max*p)}</div>`).join('')}
      </div>
      <div style="flex:1;">
        <svg width="100%" viewBox="0 0 ${W} ${H+20}" style="overflow:visible;display:block;">
          <!-- Grid -->
          ${ySteps.map(p=>`<line x1="0" y1="${H-p*H}" x2="${W}" y2="${H-p*H}" stroke="#f1f5f9" stroke-width="1"/>`).join('')}
          <!-- Area fill -->
          <polygon points="${area}" fill="${kleur}" opacity="0.08"/>
          <!-- Lijn -->
          <polyline points="${polyline}" fill="none" stroke="${kleur}" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/>
          <!-- Punten huidig jaar -->
          ${pts.map(p=>p.val>0?`<circle cx="${p.x}" cy="${p.y}" r="3" fill="${kleur}" stroke="white" stroke-width="1.5"/>`:'' ).join('')}
          <!-- Vorig jaar lijn -->
          ${data2 ? (() => {
            const pts2 = data2.map((val,i) => ({x:i*xStep, y:val?H-Math.round((val/max)*H):H, val}));
            const poly2 = pts2.map(p=>`${p.x},${p.y}`).join(' ');
            const area2 = `${pts2[0].x},${H} ${poly2} ${pts2[pts2.length-1].x},${H}`;
            return `<polygon points="${area2}" fill="${kleur2}" opacity="0.10"/>
              <polyline points="${poly2}" fill="none" stroke="${kleur2}" stroke-width="1.5" stroke-dasharray="4,3" stroke-linejoin="round" stroke-linecap="round"/>
              ${pts2.map(p=>p.val>0?`<circle cx="${p.x}" cy="${p.y}" r="2.5" fill="${kleur2}" stroke="white" stroke-width="1"/>`:'' ).join('')}`;
          })() : ''}
          <!-- X labels -->
          ${labels.map((l,i)=>`<text x="${i*xStep}" y="${H+14}" font-size="8" fill="#94a3b8" text-anchor="middle">${l}</text>`).join('')}
        </svg>
      </div>
    </div>`;
}

function taartGrafiek(data, labels, kleuren, donut=false, formatVal=null, maxHeight=180) {
  const total = data.reduce((s,v)=>s+v,0)||1;
  // Schaal SVG op basis van beschikbare hoogte
  const svgW  = Math.min(Math.round(maxHeight), 180);
  const cx = svgW/2, cy = svgW/2;
  const r      = donut ? Math.round(svgW*0.31) : Math.round(svgW*0.42);
  const stroke = donut ? Math.round(svgW*0.156) : 0;
  // Schaal lettergroottes mee
  const fsLeg  = Math.max(7, Math.min(11, Math.round(svgW * 0.061)));
  const fsPct  = Math.max(6, Math.round(svgW * 0.05));
  const fsDonut= Math.max(9, Math.round(svgW * 0.089));
  const legMB  = Math.max(2, Math.round(svgW * 0.033));
  const dotW   = Math.max(6, Math.round(svgW * 0.056));

  let angle = -Math.PI/2;
  const defaultKleuren = ['#1a364c','#d4a848','#10b981','#3b82f6','#8b5cf6','#ef4444','#f59e0b','#06b6d4','#ec4899','#84cc16','#f97316','#6366f1'];
  kleuren = kleuren || defaultKleuren;

  const arcs = data.map((val,i) => {
    const pct = val/total;
    const a1 = angle, a2 = angle + pct*2*Math.PI;
    angle = a2;
    const mid = (a1+a2)/2;
    const x1=cx+r*Math.cos(a1), y1=cy+r*Math.sin(a1);
    const x2=cx+r*Math.cos(a2), y2=cy+r*Math.sin(a2);
    const large = pct>.5?1:0;
    return {val, pct, a1, a2, mid, x1, y1, x2, y2, large, kleur:kleuren[i%kleuren.length], label:labels[i]};
  });

  return `
    <div style="display:flex;align-items:center;gap:12px;height:${svgW}px;">
      <svg width="${svgW}" height="${svgW}" viewBox="0 0 ${svgW} ${svgW}" style="flex-shrink:0;">
        ${arcs.map(arc => donut
          ? `<path d="M ${arc.x1} ${arc.y1} A ${r} ${r} 0 ${arc.large} 1 ${arc.x2} ${arc.y2}" fill="none" stroke="${arc.kleur}" stroke-width="${stroke}" stroke-linecap="butt"/>`
          : `<path d="M ${cx} ${cy} L ${arc.x1} ${arc.y1} A ${r} ${r} 0 ${arc.large} 1 ${arc.x2} ${arc.y2} Z" fill="${arc.kleur}" stroke="white" stroke-width="1.5"/>
             ${arc.pct>0.07?`<text x="${cx+(r*0.65)*Math.cos(arc.mid)}" y="${cy+(r*0.65)*Math.sin(arc.mid)}" font-size="${fsPct}" fill="white" text-anchor="middle" dominant-baseline="middle" font-weight="700">${Math.round(arc.pct*100)}%</text>`:''}`
        ).join('')}
        ${donut?`<text x="${cx}" y="${cy-fsDonut*0.4}" font-size="${fsDonut}" font-weight="800" fill="#1a364c" text-anchor="middle">${data.length}</text>
                 <text x="${cx}" y="${cy+fsDonut*0.9}" font-size="${Math.round(fsDonut*0.6)}" fill="#94a3b8" text-anchor="middle">cat.</text>`:''}
      </svg>
      <div style="flex:1;overflow:hidden;">
        ${arcs.map(arc=>`
          <div style="display:flex;align-items:center;gap:5px;margin-bottom:${legMB}px;">
            <div style="width:${dotW}px;height:${dotW}px;border-radius:${donut?'50%':'2px'};background:${arc.kleur};flex-shrink:0;"></div>
            <span style="flex:1;font-size:${fsLeg}px;color:#1e293b;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${arc.label}</span>
            <span style="font-size:${fsLeg}px;font-weight:600;color:#1a364c;white-space:nowrap;">${formatVal?formatVal(arc.val):arc.val}</span>
            <span style="font-size:${fsLeg-1}px;color:#94a3b8;width:26px;text-align:right;">${Math.round(arc.pct*100)}%</span>
          </div>`).join('')}
      </div>
    </div>`;
}

function dubbeleGrafiek(type, data1, data2, labels, kleur1, kleur2, hoogte=110) {
  if(type==='taart'||type==='donut'||type==='lijn') {
    return renderGrafiek(type, data1, labels, kleur1, hoogte);
  }
  const max = Math.max(...data1, ...data2, 1);
  const n = data1.length;
  const W = 480, H = hoogte, yW = 46, xH = 18;
  const totW = W + yW;
  const totH = H + xH + 14;

  // Per maand: groep van 2 balken met ruimte ertussen en ruimte aan de zijkanten
  const slotW   = W / n;           // breedte per maand (slot)
  const groepW  = slotW * 0.75;    // 75% voor de twee balken samen
  const tussenW = groepW * 0.10;   // 10% van groepbreedte als ruimte tussen balken
  const balkW   = (groepW - tussenW) / 2;  // breedte per balk
  const offsetX = (slotW - groepW) / 2;   // centreer groep in slot

  const ySteps = [0, 0.25, 0.5, 0.75, 1];
  const depth = type === 'balk3d' ? 4 : 0;

  return `<svg width="100%" viewBox="0 0 ${totW + depth} ${totH + depth}" style="display:block;overflow:visible;">
    <!-- Y-as labels en gridlijnen -->
    ${ySteps.map(p=>`
      <text x="${yW-4}" y="${14+H-Math.round(p*H)+3}" font-size="8" fill="#94a3b8" text-anchor="end">${fmtK(max*p)}</text>
      <line x1="${yW}" y1="${14+H-Math.round(p*H)}" x2="${totW}" y2="${14+H-Math.round(p*H)}"
        stroke="${p===0?'#cbd5e1':'#f1f5f9'}" stroke-width="${p===0?1:0.5}"/>
    `).join('')}
    <!-- Balken per maand -->
    ${data1.map((v1, i) => {
      const v2  = data2[i] || 0;
      const x0  = yW + i * slotW + offsetX;   // start van groep
      const x1  = x0;                           // balk 1 (huidig jaar)
      const x2  = x0 + balkW + tussenW;         // balk 2 (vorig jaar)
      const h1  = v1 ? Math.max(Math.round((v1/max)*H), 3) : 0;
      const h2  = v2 ? Math.max(Math.round((v2/max)*H), 3) : 0;
      const y1  = 14 + H - h1;
      const y2  = 14 + H - h2;
      const cx  = x0 + groepW / 2;

      if(type === 'balk3d') {
        return `<g>
          ${h1>0?`
            <polygon points="${x1},${y1} ${x1+depth},${y1-depth} ${x1+balkW+depth},${y1-depth} ${x1+balkW},${y1}" fill="#2d5a7a"/>
            <polygon points="${x1+balkW},${y1} ${x1+balkW+depth},${y1-depth} ${x1+balkW+depth},${y1-depth+h1} ${x1+balkW},${y1+h1}" fill="#0f2030" opacity="0.6"/>
            <rect x="${x1}" y="${y1}" width="${balkW}" height="${h1}" fill="${kleur1}"/>
          `:''}
          ${h2>0?`
            <polygon points="${x2},${y2} ${x2+depth},${y2-depth} ${x2+balkW+depth},${y2-depth} ${x2+balkW},${y2}" fill="#8faabb" opacity="0.8"/>
            <polygon points="${x2+balkW},${y2} ${x2+balkW+depth},${y2-depth} ${x2+balkW+depth},${y2-depth+h2} ${x2+balkW},${y2+h2}" fill="#8faabb" opacity="0.4"/>
            <rect x="${x2}" y="${y2}" width="${balkW}" height="${h2}" fill="${kleur2}"/>
          `:''}
          <text x="${cx}" y="${14+H+xH-2}" font-size="8" fill="#94a3b8" text-anchor="middle">${labels[i]}</text>
        </g>`;
      }
      return `<g>
        ${h1>0?`<rect x="${x1}" y="${y1}" width="${balkW}" height="${h1}" rx="2" fill="${kleur1}"/>
          <text x="${x1+balkW/2}" y="${y1-3}" font-size="7" fill="#64748b" text-anchor="middle">${fmtK(v1)}</text>`:''}
        ${h2>0?`<rect x="${x2}" y="${y2}" width="${balkW}" height="${h2}" rx="2" fill="${kleur2}"/>`:''}
        <text x="${cx}" y="${14+H+xH-2}" font-size="8" fill="#94a3b8" text-anchor="middle">${labels[i]}</text>
      </g>`;
    }).join('')}
  </svg>`;
}

function hBalk(items, formatVal, max, prevItems, compact=false) {
  if(!items||items.length===0) return '<p style="color:#94a3b8;font-size:11px;padding:8px 0;">Geen data</p>';
  const m = max || Math.max(...items.map(i=>i.v), 1);
  const kleuren = ['#1a364c','#24496a','#2d5a7a','#4a7a9b','#7aaccf','#c2e6ef'];
  const prevMap = {};
  if(prevItems && prevItems.length) {
    prevItems.forEach(p => { if(p && p.l != null) prevMap[String(p.l)] = p.v; });
  }
  const heeftPrev = Object.keys(prevMap).length > 0;

  // compact=true: kleinere rijen, minder lettergrootte (voor goederensoorten in liggend)
  const rijen  = items.length;
  const rijH   = compact ? (heeftPrev ? 14 : 10) : (heeftPrev ? 22 : 16);
  const fs     = compact ? 8 : 10;
  const bH     = compact ? 5 : 7;
  const bHPrev = compact ? 3 : 5;
  const labelW = compact ? 80 : 90;
  const valW   = compact ? 32 : 44;
  const barX   = labelW + 4;
  const totW   = 460;
  const barW   = totW - barX - valW - 4;
  const svgH   = rijen * rijH + 4;

  return `<svg width="100%" viewBox="0 0 ${totW} ${svgH}" style="display:block;overflow:visible;">
    ${items.map((item, i) => {
      const y       = i * rijH + 2;
      const pct     = Math.round((item.v / m) * barW);
      const prevV   = prevMap[String(item.l)];
      const heeftRij= heeftPrev && prevV !== undefined && prevV !== null;
      const prevPct = heeftRij ? Math.round((prevV / m) * barW) : 0;
      const delta   = heeftRij ? item.v - prevV : null;
      const kleur   = kleuren[i % kleuren.length];
      const deltaKleur = delta > 0 ? '#10b981' : delta < 0 ? '#ef4444' : '#94a3b8';
      const deltaText  = delta !== null ? `${delta > 0 ? '+' : ''}${delta}` : '';

      return `
        <text x="${labelW}" y="${y + Math.round(rijH*0.55)}" font-size="${fs}" fill="#1e293b"
          text-anchor="end" dominant-baseline="middle">${item.l.length > 13 ? item.l.slice(0,13)+'…' : item.l}</text>
        <rect x="${barX}" y="${y + 1}" width="${barW}" height="${bH}" rx="2" fill="#f1f5f9"/>
        <rect x="${barX}" y="${y + 1}" width="${pct}" height="${bH}" rx="2" fill="${kleur}"/>
        ${heeftRij ? `
          <rect x="${barX}" y="${y + bH + 3}" width="${barW}" height="${bHPrev}" rx="2" fill="#f1f5f9"/>
          <rect x="${barX}" y="${y + bH + 3}" width="${prevPct}" height="${bHPrev}" rx="2" fill="#94a3b8" opacity="0.6"/>
        ` : ''}
        <text x="${totW - valW + 2}" y="${y + Math.round(rijH*0.48)}" font-size="${fs}" font-weight="600"
          fill="#1a364c" dominant-baseline="middle">${formatVal ? formatVal(item.v) : item.v}</text>
        ${delta !== null ? `<text x="${totW - 2}" y="${y + rijH - 2}" font-size="${fs-1}"
          fill="${deltaKleur}" text-anchor="end" dominant-baseline="middle">${deltaText}</text>` : ''}
      `;
    }).join('')}
  </svg>`;
}

function renderRanking(items, formatVal, type, maxHeight, prevItems) {
  type = type || 'hbalk';
  if(!items||items.length===0) return '<p style="color:#94a3b8;font-size:11px;padding:8px 0;">Geen data</p>';
  if(type==='taart'||type==='donut') {
    return taartGrafiek(items.map(i=>i.v), items.map(i=>i.l), null, type==='donut', formatVal, maxHeight||130);
  }
  return hBalk(items, formatVal, null, prevItems);
}

function renderGrafiek(type, data, labels, kleur, hoogte=130, data2=null) {
  const nonZero = data.filter(Boolean);
  if(nonZero.length===0) return '<p style="color:#94a3b8;font-size:11px;padding:12px 0;">Geen data beschikbaar</p>';
  if(type==='lijn')    return lijnGrafiek(data, labels, kleur, hoogte, data2, '#c2e6ef');
  if(type==='taart')   return taartGrafiek(data, labels, null, false, null, hoogte||130);
  if(type==='donut')   return taartGrafiek(data, labels, null, true, null, hoogte||130);
  if(type==='balk3d')  return balkGrafiek3D(data, labels, kleur, hoogte);
  return balkGrafiek(data, labels, kleur, hoogte);
}


export { balkGrafiek, balkGrafiek3D, lijnGrafiek, taartGrafiek, dubbeleGrafiek, hBalk, renderRanking, renderGrafiek };
